import React, { useState, useRef, useEffect } from 'react';
import { Sliders, X, Zap, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { useLanguage } from '../i18n';
import { useTheme } from '../context/ThemeContext';

const CatalogFilters = ({ bikes, onFilterChange, activeFilters, onDropdownChange }) => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Notify parent when dropdown state changes
  useEffect(() => {
    if (onDropdownChange) {
      onDropdownChange(!!openDropdown);
    }
  }, [openDropdown, onDropdownChange]);
  
  const handleSetOpenDropdown = (value) => {
    setOpenDropdown(value);
  };
  const dropdownRefs = {
    power: useRef(null),
    price: useRef(null),
    features: useRef(null),
    motorType: useRef(null)
  };

  // Get unique values
  const powerOptions = [
    { value: '42', label: '42 Nm', description: language === 'it' ? 'Base' : language === 'es' ? 'Básico' : 'Basic' },
    { value: '80', label: '80 Nm', description: language === 'it' ? 'Potenziato' : language === 'es' ? 'Potenciado' : 'Enhanced' },
    { value: '130', label: '130 Nm', description: language === 'it' ? 'Premium' : language === 'es' ? 'Premium' : 'Premium' },
  ];

  const priceRanges = [
    { value: '0-1500', label: '< €1,500' },
    { value: '1500-2000', label: '€1,500 - €2,000' },
    { value: '2000-3000', label: '€2,000 - €3,000' },
    { value: '3000+', label: '> €3,000' },
  ];

  const featureOptions = [
    { value: 'suspension', label: language === 'it' ? 'Full Suspension' : language === 'es' ? 'Suspensión Completa' : 'Full Suspension' },
    { value: 'folding', label: language === 'it' ? 'Pieghevole' : language === 'es' ? 'Plegable' : 'Folding' },
    { value: 'promo', label: language === 'it' ? 'In Promozione' : language === 'es' ? 'En Oferta' : 'On Sale' },
  ];

  const motorTypeOptions = [
    { value: 'centrale', label: language === 'it' ? 'Centrale' : language === 'es' ? 'Central' : 'Central' },
    { value: 'posteriore', label: language === 'it' ? 'Posteriore' : language === 'es' ? 'Trasero' : 'Rear' },
  ];

  const labels = {
    it: { filters: 'Filtri', power: 'Potenza Motore', price: 'Prezzo', features: 'Caratteristiche', motorType: 'Tipologia Motore', clearAll: 'Cancella filtri', results: 'risultati' },
    en: { filters: 'Filters', power: 'Motor Power', price: 'Price', features: 'Features', motorType: 'Motor Type', clearAll: 'Clear filters', results: 'results' },
    es: { filters: 'Filtros', power: 'Potencia Motor', price: 'Precio', features: 'Características', motorType: 'Tipo Motor', clearAll: 'Borrar filtros', results: 'resultados' }
  };
  const l = labels[language] || labels.en;

  const activeCount = Object.values(activeFilters).flat().filter(Boolean).length;

  const toggleFilter = (type, value) => {
    const current = activeFilters[type] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onFilterChange({ ...activeFilters, [type]: updated });
  };

  const clearFilters = () => {
    onFilterChange({ power: [], price: [], features: [], motorType: [] });
    handleSetOpenDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        handleSetOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDropdown]);

  const getActiveCount = (type) => {
    return (activeFilters[type] || []).length;
  };

  const FilterDropdown = ({ title, name, options, icon: Icon }) => {
    const isOpen = openDropdown === name;
    const count = getActiveCount(name);
    
    return (
      <div className="relative" ref={dropdownRefs[name]}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSetOpenDropdown(isOpen ? null : name);
          }}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
            isOpen || count > 0
              ? isDark
                ? 'bg-[#B5A16A]/20 border-[#B5A16A] text-[#B5A16A]'
                : 'bg-[#B5A16A]/10 border-[#B5A16A] text-[#8a7a52]'
              : isDark
                ? 'bg-[#12121a] border-[#2a2a3a] text-white hover:border-[#B5A16A]/50'
                : 'bg-white border-gray-200 text-gray-700 hover:border-[#B5A16A]/30'
          }`}
        >
          {Icon && <Icon className="w-4 h-4" />}
          <span className="font-medium text-sm">{title}</span>
          {count > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isDark ? 'bg-[#B5A16A] text-white' : 'bg-[#8a7a52] text-white'
            }`}>
              {count}
            </span>
          )}
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {isOpen && (
          <div 
            className={`absolute top-full left-0 mt-2 z-[9999] min-w-[200px] max-w-[250px] rounded-xl border shadow-2xl ${
              isDark 
                ? 'bg-[#12121a] border-[#2a2a3a]'
                : 'bg-white border-gray-200 shadow-xl'
            }`}
            style={{ 
              position: 'absolute',
              zIndex: 9999,
              isolation: 'isolate'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-3 space-y-2">
              {options.map((option) => {
                const isActive = (activeFilters[name] || []).includes(option.value);
                return (
                  <label
                    key={option.value}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFilter(name, option.value);
                    }}
                    className="flex items-center gap-3 cursor-pointer group py-1.5 px-2 rounded-lg hover:bg-opacity-10 hover:bg-[#B5A16A] transition-colors"
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                        isActive
                          ? 'bg-[#B5A16A] border-[#B5A16A]'
                          : isDark 
                            ? 'border-[#B5A16A]/50 group-hover:border-[#B5A16A]'
                            : 'border-gray-300 group-hover:border-[#B5A16A]'
                      }`}
                    >
                      {isActive && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
                        {option.label}
                      </span>
                      {option.description && (
                        <span className={`text-xs block ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                          {option.description}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-6 relative z-50">
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-4">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-4 py-3 border rounded-xl w-full justify-center ${
            isDark 
              ? 'bg-[#12121a] border-[#2a2a3a] text-white'
              : 'bg-white border-gray-200 text-gray-900'
          }`}
        >
          <Sliders className="w-5 h-5" />
          <span>{l.filters}</span>
          {activeCount > 0 && (
            <span className="bg-[#B5A16A] text-white text-xs px-2 py-0.5 rounded-full">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* Horizontal Filter Bar - Desktop Only */}
      <div className="hidden lg:flex items-center gap-3 flex-wrap relative z-50">
        <div className={`flex items-center gap-3 flex-wrap relative ${
          isDark 
            ? 'bg-[#12121a] border-[#2a2a3a]'
            : 'bg-white border-gray-200'
        } border rounded-2xl p-4 w-full lg:w-auto`}>
          {/* Title */}
          <div className="flex items-center gap-2 mr-2">
            <Sliders className={`w-5 h-5 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
            <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {l.filters}:
            </span>
          </div>

          {/* Filter Dropdowns */}
          <FilterDropdown
            title={l.power}
            name="power"
            options={powerOptions}
            icon={Zap}
          />
          
          <FilterDropdown
            title={l.price}
            name="price"
            options={priceRanges}
          />
          
          <FilterDropdown
            title={l.features}
            name="features"
            options={featureOptions}
          />
          
          <FilterDropdown
            title={l.motorType}
            name="motorType"
            options={motorTypeOptions}
            icon={Settings}
          />

          {/* Clear Filters Button */}
          {activeCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className={`flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                isDark
                  ? 'text-[#B5A16A] hover:bg-[#B5A16A]/20'
                  : 'text-[#8a7a52] hover:bg-[#B5A16A]/10'
              }`}
            >
              <X className="w-4 h-4" />
              {l.clearAll}
            </button>
          )}
        </div>

        {/* Results count */}
        <div className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          <span className="font-medium">{bikes.length}</span> {l.results}
        </div>
      </div>

      {/* Mobile Filter Panel */}
      {isOpen && (
        <div className={`lg:hidden mt-4 border rounded-2xl p-4 relative z-50 ${
          isDark 
            ? 'bg-[#12121a] border-[#2a2a3a]'
            : 'bg-white border-gray-200'
        }`}>
          <div className="space-y-4">
            <FilterDropdown
              title={l.power}
              name="power"
              options={powerOptions}
              icon={Zap}
            />
            
            <FilterDropdown
              title={l.price}
              name="price"
              options={priceRanges}
            />
            
            <FilterDropdown
              title={l.features}
              name="features"
              options={featureOptions}
            />
            
            <FilterDropdown
              title={l.motorType}
              name="motorType"
              options={motorTypeOptions}
              icon={Settings}
            />

            {activeCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${
                  isDark
                    ? 'text-[#B5A16A] bg-[#B5A16A]/20'
                    : 'text-[#8a7a52] bg-[#B5A16A]/10'
                }`}
              >
                <X className="w-4 h-4" />
                {l.clearAll}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogFilters;
